import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import DraggableModal from "../DraggableModal";
import SearchSelect from "../SearchSelect";
import EmailDialog from "../EmailDialog";
import { htmlToPdfBlob, safeFilename } from "../../pdfRenderer";
import { mergePdfBlobs } from "../../pdfMerge";
import { loadLS, escHtml, fmtDateShort, getCompanyName, reportStyles, reportHeader, reportFooter, openPreviewWindow, downloadBlob, NAVY, YELLOW_HL } from "./auditCommon";
import { loadFromSupabase } from "../../supabaseDB";
import { appendDisclosureByAccount } from "../emailHelpers";
import { buildReceivingSheetHTML, buildCartaPorteHTML } from "../ShipmentRegistry";
import { buildInvoicePrintHTML, buildPackingListHTML } from "../InvoiceRegistry";

/* ============================================================================
 * Traceability Sample Report
 *
 * For a single picked shipment + downstream invoice, builds:
 *   Page 1: a flow-diagram cover page (Grower → Shipper → Customs →
 *           Warehouse → Client) with the key linking numbers
 *           highlighted in yellow + cross-references to the annexes.
 *   Annexes (each merged into the final PDF):
 *     A — Shipper Invoice  (from shipment.invoiceFile.data, if PDF)
 *     B — Bill of Lading  (Carta Porte, generated by ShipmentRegistry)
 *     C — Receiving Sheet (generated by ShipmentRegistry)
 *     D — Packing List    (generated by InvoiceRegistry)
 *     E — Customer Invoice (GFI invoice, generated by InvoiceRegistry)
 *
 * Annex A is only included if the shipper invoice on file is itself a
 * PDF; image / unknown formats are skipped (logged in the cover page
 * note line). The other four are always available because they're
 * generated from current data.
 *
 * The auditor can email the merged bundle to themselves directly from
 * this panel. The "selected" invoice defaults to the first invoice
 * that mentions the same lot number; the user can change it.
 * ========================================================================= */

const T = {
  en: {
    title: "Traceability Sample",
    sub: "Chain-of-custody report",
    selectShipment: "Shipment / Lot",
    selectInvoice: "Customer Invoice",
    selectShipmentPh: "Choose a shipment",
    selectInvoicePh: "Choose an invoice (auto-detected if blank)",
    btnPreview: "Preview cover", btnPdf: "Print combined PDF", btnEmail: "Email", close: "Close",
    sending: "Building combined PDF...",
    emailSubject: "Traceability Sample",
    /* Cover layout */
    flow_title: "Chain of Custody",
    box_grower: "GROWER",
    box_shipper: "SHIPPER (Mexico)",
    box_customs: "U.S. CUSTOMS",
    box_warehouse: "COLD STORAGE",
    box_client: "CLIENT (USA)",
    annexA: "Annex A — Shipper Invoice",
    annexB: "Annex B — Bill of Lading",
    annexC: "Annex C — Receiving Sheet",
    annexD: "Annex D — Packing List",
    annexE: "Annex E — Customer Invoice",
    lblLot: "GFI Lot", lblProforma: "Proforma", lblShipperInv: "Shipper Invoice",
    lblEntry: "Customs Entry", lblPO: "PO #", lblPU: "PU #",
    lblDate: "Date",
    lblShipDate: "Ship Date", lblArrival: "Arrival", lblPickup: "Pickup",
    productSummary: "Product",
    chooseFirst: "Pick a shipment to build the report.",
    noInvoice: "No invoice selected (and none auto-detected) — Annexes D and E will be skipped.",
    noShipperPdf: "Shipper invoice on file is not a PDF — Annex A skipped.",
  },
  es: {
    title: "Muestra de Trazabilidad",
    sub: "Reporte de cadena de custodia",
    selectShipment: "Embarque / Lote",
    selectInvoice: "Factura del Cliente",
    selectShipmentPh: "Selecciona un embarque",
    selectInvoicePh: "Selecciona una factura (auto si vacío)",
    btnPreview: "Vista previa de la carátula", btnPdf: "Imprimir PDF combinado", btnEmail: "Enviar Correo", close: "Cerrar",
    sending: "Generando PDF combinado...",
    emailSubject: "Muestra de Trazabilidad",
    flow_title: "Cadena de Custodia",
    box_grower: "PRODUCTOR",
    box_shipper: "EXPORTADOR (México)",
    box_customs: "ADUANA EE.UU.",
    box_warehouse: "FRIGORÍFICO",
    box_client: "CLIENTE (USA)",
    annexA: "Anexo A — Factura del Exportador",
    annexB: "Anexo B — Carta Porte",
    annexC: "Anexo C — Hoja de Recibo",
    annexD: "Anexo D — Packing List",
    annexE: "Anexo E — Factura al Cliente",
    lblLot: "Lote GFI", lblProforma: "Proforma", lblShipperInv: "Factura del Exportador",
    lblEntry: "Entry Aduanal", lblPO: "PO #", lblPU: "PU #",
    lblDate: "Fecha",
    lblShipDate: "Fecha de Embarque", lblArrival: "Llegada", lblPickup: "Pickup",
    productSummary: "Producto",
    chooseFirst: "Selecciona un embarque para generar el reporte.",
    noInvoice: "Sin factura seleccionada (ni auto-detectada) — se omitirán los Anexos D y E.",
    noShipperPdf: "La factura del exportador no es PDF — se omitirá el Anexo A.",
  },
};

const btnPrimary = { padding: "8px 16px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 13, fontWeight: 700 };
const btnSecondary = { padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 };

/* Helpers */
function shipperNameFor(shipment, suppliers) {
  if (!shipment) return "";
  if (shipment.shipperName) return shipment.shipperName;
  var s = (suppliers || []).find(function (x) { return String(x.id) === String(shipment.supplierId); });
  return s ? (s.shortName || s.fullName || s.name || "") : "";
}
function clientNameFor(invoice, clients) {
  if (!invoice) return "";
  var c = (clients || []).find(function (x) { return String(x.id) === String(invoice.clientId); });
  if (c) return c.fullName || c.shortName || c.name || invoice.clientName || "";
  return invoice.clientName || "";
}
function warehouseNameFor(shipment, warehouses) {
  if (!shipment) return "";
  if (shipment.warehouseName) return shipment.warehouseName;
  var w = (warehouses || []).find(function (x) { return String(x.id) === String(shipment.warehouseId); });
  return w ? (w.shortName || w.fullName || "") : "";
}
function growerNameFor(shipment, growers) {
  /* The shipment doesn't carry a single grower id; pallets list grower
     codes. We surface a comma-joined list of grower names that appear
     in the pallet manifest. */
  if (!shipment || !Array.isArray(shipment.palletManifest)) return "";
  var codes = {};
  shipment.palletManifest.forEach(function (p) {
    var c = p && p.growerCode;
    if (c) codes[String(c)] = true;
  });
  var arr = Object.keys(codes);
  if (arr.length === 0) return "";
  var matched = arr.map(function (code) {
    var g = (growers || []).find(function (x) { return String(x.producerCode || x.code) === code; });
    if (!g) return code;
    if (g.personType === "moral") return g.razonSocial || code;
    return [g.nombre, g.apPaterno, g.apMaterno].filter(Boolean).join(" ") || code;
  });
  return matched.join(", ");
}

function findAutoInvoice(shipment, invoices) {
  if (!shipment) return null;
  /* Heuristic: any invoice whose lot matches the shipment lot, OR
     whose lines reference any of the same productIds with overlapping
     dates. Lot match is the cheapest + most reliable signal. */
  var lot = shipment.lot;
  if (!lot) return null;
  var match = (invoices || []).find(function (inv) {
    if (String(inv.lot) === String(lot)) return true;
    if (Array.isArray(inv.lots) && inv.lots.indexOf(lot) >= 0) return true;
    return false;
  });
  return match || null;
}

/* ============================================================================
 * Cover page HTML (flow diagram + key data)
 * ========================================================================= */
function buildCoverHTML(ctx, audit, lang) {
  var t = T[lang === "es" ? "es" : "en"];
  var ES = lang === "es";
  var s = ctx.shipment;
  var inv = ctx.invoice;
  var shipperName = shipperNameFor(s, ctx.suppliers);
  var clientName  = clientNameFor(inv, ctx.clients);
  var whName      = warehouseNameFor(s, ctx.warehouses);
  var growerName  = growerNameFor(s, ctx.growers);

  /* Productos resumidos (de las líneas del shipment, solo orgánicos). */
  var products = (s.lines || []).filter(function (l) {
    return String(l.category || "").toLowerCase() === "organic" || String(l.category || "").toLowerCase() === "og";
  }).map(function (l) {
    return (l.productDescription || l.productName || "") + " · " + (l.cases || 0) + " " + (ES ? "cajas" : "cases");
  }).join("<br>");

  var entry = s.entryNumber || "";

  var h = '';
  h += '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escHtml(t.title) + '</title><style>' + reportStyles();
  /* Cover-specific overrides to keep the whole sheet on ONE letter page.
     The earlier sizes (min-height:120, padding:8/10, section margin-top:16,
     arrows 20px) pushed the annex list onto a second mostly-empty sheet.
     Compressed values target a 9-inch usable area (0.4in margins × 2 = 0.8in
     bleed off an 11-inch letter sheet, leaving 10.2in tall). */
  h += ' @page{size:letter;margin:0.4in}';
  h += ' .page{padding:0}';
  h += ' .header{padding-bottom:8px;margin-bottom:10px}';
  h += ' .section{margin-top:10px}';
  h += ' .section-title{margin-bottom:4px;padding-bottom:2px;font-size:10px}';
  h += ' .flow-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:6px;margin:8px 0 4px 0}';
  h += ' .flow-box{border:1.5px solid ' + NAVY + ';border-radius:6px;padding:6px 8px;background:#fff;font-size:9px;min-height:96px}';
  h += ' .flow-box .heading{font-size:8.5px;font-weight:700;color:' + NAVY + ';text-transform:uppercase;letter-spacing:0.8px;text-align:center;padding-bottom:3px;border-bottom:1px solid #d1d5db;margin-bottom:4px}';
  h += ' .flow-box .row{margin:2px 0;line-height:1.25}';
  h += ' .flow-box .lbl{font-size:8px;color:#666;display:block}';
  h += ' .flow-box .val{font-weight:600;color:#1a1a1a;font-family:"JetBrains Mono",monospace;font-size:9.5px}';
  h += ' .arrows-row{display:grid;grid-template-columns:repeat(4,1fr);gap:6px;margin-bottom:4px}';
  h += ' .arrows-row .arrow{font-size:16px;color:' + NAVY + ';text-align:center;line-height:1}';
  h += ' .anx-list{margin-top:6px;font-size:10px}';
  h += ' .anx-list .anx{padding:4px 8px;border-bottom:1px solid #e5e7eb;display:flex;justify-content:space-between}';
  h += ' .footer{margin-top:12px;padding-top:5px}';
  /* Hard cap for the first page when previewed in an iframe — prints
     don't honor max-height but the visual preview is bounded so the
     auditor sees what will actually print. */
  h += ' @media print{ .page{ max-height: 10in; overflow: hidden } }';
  h += '</style></head><body><div class="page">';
  /* Pass audit as the 4th arg so reportHeader renders the audit
     banner + period prominently in the cover's right column. The
     legacy subtitle slot stays empty — the banner replaces it. */
  h += reportHeader(t.title, "", "", audit);

  /* Key data strip across the top of the cover */
  h += '<div class="section"><div class="section-title">' + escHtml(t.flow_title) + '</div>';

  /* GFI's audit chain of custody starts at the shipper — growers feed
     into the shipper but their compliance is tracked in the per-grower
     audit report, not this trace. Four boxes: Shipper → Customs →
     Cold Storage → Client. */
  h += '<div class="flow-grid">';
  /* SHIPPER */
  h += '<div class="flow-box">';
  h += '<div class="heading">' + escHtml(t.box_shipper) + '</div>';
  h += '<div class="row"><span class="lbl">' + escHtml(ES ? "Nombre" : "Name") + '</span><span class="val">' + escHtml(shipperName || "—") + '</span></div>';
  h += '<div class="row"><span class="lbl">' + escHtml(t.lblProforma) + '</span><span class="val hl">' + escHtml(s.proforma || "—") + '</span></div>';
  h += '<div class="row"><span class="lbl">' + escHtml(t.lblShipperInv) + '</span><span class="val hl">' + escHtml(s.shipperInvoice || "—") + '</span></div>';
  h += '<div class="row"><span class="lbl">' + escHtml(t.lblShipDate) + '</span><span class="val">' + escHtml(fmtDateShort(s.dateDeparture) || "—") + '</span></div>';
  h += '</div>';
  /* CUSTOMS */
  h += '<div class="flow-box">';
  h += '<div class="heading">' + escHtml(t.box_customs) + '</div>';
  h += '<div class="row"><span class="lbl">' + escHtml(t.lblEntry) + '</span><span class="val hl">' + escHtml(entry || "—") + '</span></div>';
  h += '<div class="row"><span class="lbl">' + escHtml(t.lblLot) + '</span><span class="val hl">' + escHtml(s.lot || "—") + '</span></div>';
  h += '<div class="row"><span class="lbl">' + escHtml(t.lblDate) + '</span><span class="val">' + escHtml(fmtDateShort(s.dateArrival) || "—") + '</span></div>';
  h += '</div>';
  /* WAREHOUSE */
  h += '<div class="flow-box">';
  h += '<div class="heading">' + escHtml(t.box_warehouse) + '</div>';
  h += '<div class="row"><span class="lbl">' + escHtml(ES ? "Bodega" : "Warehouse") + '</span><span class="val">' + escHtml(whName || "—") + '</span></div>';
  h += '<div class="row"><span class="lbl">' + escHtml(t.lblArrival) + '</span><span class="val">' + escHtml(fmtDateShort(s.dateArrival) || "—") + '</span></div>';
  h += '</div>';
  /* CLIENT */
  h += '<div class="flow-box">';
  h += '<div class="heading">' + escHtml(t.box_client) + '</div>';
  h += '<div class="row"><span class="lbl">' + escHtml(ES ? "Nombre" : "Name") + '</span><span class="val">' + escHtml(clientName || "—") + '</span></div>';
  if (inv) {
    h += '<div class="row"><span class="lbl">' + escHtml(t.lblPO) + '</span><span class="val hl">' + escHtml(inv.clientRef || inv.poNumber || "—") + '</span></div>';
    h += '<div class="row"><span class="lbl">' + escHtml(t.lblPU) + '</span><span class="val hl">' + escHtml(inv.puNumber || "—") + '</span></div>';
    h += '<div class="row"><span class="lbl">' + escHtml(t.lblPickup) + '</span><span class="val">' + escHtml(fmtDateShort(inv.pickupDate || inv.invoiceDate) || "—") + '</span></div>';
  }
  h += '</div>';
  h += '</div>'; /* flow-grid */

  /* Arrows row — 4 boxes ⇒ 3 arrows after the first column. */
  h += '<div class="arrows-row"><div></div><div class="arrow">→</div><div class="arrow">→</div><div class="arrow">→</div></div>';
  h += '</div>'; /* section */

  /* Product summary */
  h += '<div class="section"><div class="section-title">' + escHtml(t.productSummary) + '</div>';
  h += '<div style="font-size:11px;line-height:1.6">' + (products || '<span style="color:#888;font-style:italic">' + escHtml(ES ? "Sin productos orgánicos en el embarque" : "No organic products on this shipment") + '</span>') + '</div>';
  h += '</div>';

  /* Annex list */
  h += '<div class="section"><div class="section-title">' + escHtml(ES ? "Anexos" : "Annexes") + '</div>';
  h += '<div class="anx-list">';
  h += '<div class="anx"><span>' + escHtml(t.annexA) + '</span><span style="color:' + (ctx.hasShipperPdf ? '#047857' : '#DC2626') + '">' + escHtml(ctx.hasShipperPdf ? (ES ? "Incluido" : "Included") : t.noShipperPdf) + '</span></div>';
  h += '<div class="anx"><span>' + escHtml(t.annexB) + '</span><span style="color:#047857">' + escHtml(ES ? "Incluido" : "Included") + '</span></div>';
  h += '<div class="anx"><span>' + escHtml(t.annexC) + '</span><span style="color:#047857">' + escHtml(ES ? "Incluido" : "Included") + '</span></div>';
  h += '<div class="anx"><span>' + escHtml(t.annexD) + '</span><span style="color:' + (inv ? '#047857' : '#DC2626') + '">' + escHtml(inv ? (ES ? "Incluido" : "Included") : (ES ? "Sin factura" : "No invoice")) + '</span></div>';
  h += '<div class="anx"><span>' + escHtml(t.annexE) + '</span><span style="color:' + (inv ? '#047857' : '#DC2626') + '">' + escHtml(inv ? (ES ? "Incluido" : "Included") : (ES ? "Sin factura" : "No invoice")) + '</span></div>';
  h += '</div></div>';

  h += reportFooter(audit, lang);
  h += '</div></body></html>';
  return h;
}

/* ============================================================================
 * Panel UI
 * ========================================================================= */
export default function TraceabilityPanel({ lang, audit, onClose, onLogReport, initialParams }) {
  const t = T[lang === "es" ? "es" : "en"];
  const [shipments] = useState(() => loadLS("gfi-shipments", []));
  const [invoices]  = useState(() => loadLS("gfi-invoices", []));
  const [lookups] = useState(() => ({
    suppliers:  loadLS("gfi-suppliers", []),
    warehouses: loadLS("gfi-warehouses", []),
    clients:    loadLS("gfi-clients", []),
    growers:    loadLS("gfi-growers", []),
    brokers:    loadLS("gfi-customs-brokers", []),
    seasons:    loadLS("gfi-seasons", []),
    commodities: loadLS("gfi-products", []),
    company:    (function () { try { return JSON.parse(localStorage.getItem("gfi-company-profile") || "{}"); } catch (e) { return {}; } })(),
  }));
  /* Restore the picked shipment/invoice when the registry re-opens
     an old report. Manual changes by the user afterwards still win. */
  const [shipmentId, setShipmentId] = useState((initialParams && initialParams.shipmentId) || "");
  const [invoiceId, setInvoiceId]   = useState((initialParams && initialParams.invoiceId)   || "");
  const [busy, setBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const lastBlobRef = useRef(null);
  const lastFilenameRef = useRef("");
  /* When initialParams supplies an invoiceId we must NOT clobber it
     with the auto-detected match (the auto-detect effect runs on
     shipment-id change). Track whether the initial restore has been
     applied; the auto-detect only fires on subsequent user changes. */
  const initialRestoredRef = useRef(!!(initialParams && initialParams.invoiceId));

  /* Shipments dropdown — only those with at least one organic line.
     Sort by lot DESC numerically. The previous sort used `b.date`
     vs `a.date`, but `date` is empty on portal-promoted shipments
     (they use dateArrival / dateDeparture) so those rows piled up
     at the bottom of the dropdown. Lot is a monotonically-increasing
     integer per business rules, so newer shipments naturally sit
     at the top. */
  const shipmentOptions = useMemo(() => {
    return shipments
      .filter(function (s) {
        return (s.lines || []).some(function (l) { var c = String(l.category || "").toLowerCase(); return c === "organic" || c === "og"; });
      })
      .slice()
      .sort(function (a, b) { return (parseInt(b.lot, 10) || 0) - (parseInt(a.lot, 10) || 0); })
      .map(function (s) {
        var name = shipperNameFor(s, lookups.suppliers);
        var d = s.dateArrival || s.dateDeparture || s.date || "";
        return { value: String(s.id), label: (s.lot ? "Lot " + s.lot + " · " : "") + d + " · " + name + " · Inv " + (s.shipperInvoice || "—") };
      });
  }, [shipments, lookups.suppliers]);

  const shipment = useMemo(() => shipments.find(function (x) { return String(x.id) === String(shipmentId); }) || null, [shipments, shipmentId]);

  /* Auto-detect an invoice when the user picks a shipment. The user
     can override with the invoice dropdown. Skip on the very first
     run when initialParams pinned an invoiceId (Reopen flow). */
  const autoInvoice = useMemo(() => findAutoInvoice(shipment, invoices), [shipment, invoices]);
  useEffect(() => {
    if (initialRestoredRef.current) { initialRestoredRef.current = false; return; }
    if (shipment) {
      if (autoInvoice) setInvoiceId(String(autoInvoice.id));
      else setInvoiceId("");
    } else {
      setInvoiceId("");
    }
  }, [shipmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Invoices dropdown — sort by lot DESC numerically (same reason
     as the shipments dropdown above). Falls back to puNumber /
     invoiceNumber when lot is missing so legacy invoices don't all
     collapse to position 0. */
  const invoiceOptions = useMemo(() => {
    return invoices
      .slice()
      .sort(function (a, b) {
        var al = parseInt(a.lot, 10);
        var bl = parseInt(b.lot, 10);
        if (!isNaN(bl) || !isNaN(al)) return (isNaN(bl) ? -1 : bl) - (isNaN(al) ? -1 : al);
        var ap = parseInt(a.puNumber || a.invoiceNumber, 10) || 0;
        var bp = parseInt(b.puNumber || b.invoiceNumber, 10) || 0;
        return bp - ap;
      })
      .map(function (inv) {
        var cl = clientNameFor(inv, lookups.clients);
        return { value: String(inv.id), label: (inv.invoiceNumber ? "INV " + inv.invoiceNumber + " · " : "") + (inv.invoiceDate || inv.date || "") + " · " + cl + (inv.lot ? " · Lot " + inv.lot : "") };
      });
  }, [invoices, lookups.clients]);

  const invoice = useMemo(() => invoices.find(function (x) { return String(x.id) === String(invoiceId); }) || null, [invoices, invoiceId]);

  const shipperPdfBlob = useMemo(() => {
    if (!shipment || !shipment.invoiceFile) return null;
    var f = shipment.invoiceFile;
    /* Three possible payload sources, in order of preference:
         1. f.url   — Storage public URL (new path; PDFs uploaded via
                       the `shipment-docs` bucket).
         2. f.data  — legacy inline base64 data URL (pre-Storage rows).
       Either string is returned as the "presence signal" for the
       cover page; downstream PDF merging fetches the URL or decodes
       the data URL as appropriate. */
    if (typeof f.url === "string" && f.url) return f.url;
    if (typeof f.data === "string" && f.data.indexOf("data:application/pdf") === 0) return f.data;
    return null;
  }, [shipment]);

  const buildCover = useCallback(() => {
    if (!shipment) return "<!doctype html><body style=\"font-family:sans-serif;padding:40px;color:#888\">" + escHtml(t.chooseFirst) + "</body>";
    return buildCoverHTML({
      shipment: shipment, invoice: invoice,
      suppliers: lookups.suppliers, warehouses: lookups.warehouses,
      clients: lookups.clients, growers: lookups.growers,
      hasShipperPdf: !!shipperPdfBlob,
    }, audit, lang);
  }, [shipment, invoice, lookups, shipperPdfBlob, audit, lang, t.chooseFirst]);

  const handlePreview = () => openPreviewWindow(buildCover());

  const buildCombinedPdf = useCallback(async () => {
    if (!shipment) throw new Error("No shipment selected");
    const items = [];
    /* Cover */
    const coverBlob = await htmlToPdfBlob(buildCover(), {});
    items.push({ name: "cover", blob: coverBlob });
    /* Annex A — shipper invoice (if PDF). */
    if (shipperPdfBlob) {
      try {
        const aBlob = await fetch(shipperPdfBlob).then(function (r) { return r.blob(); });
        items.push({ name: "annex_a_shipper_invoice", blob: aBlob });
      } catch (e) { console.warn("[traceability] annex A fetch failed:", e && e.message); }
    }
    /* Annex B — Carta Porte (Bill of Lading) */
    try {
      const html = buildCartaPorteHTML(shipment, lookups.suppliers, lookups.warehouses, lookups.commodities, lookups.brokers, lookups.seasons, lang, "");
      const blob = await htmlToPdfBlob(html, { orientation: "portrait" });
      items.push({ name: "annex_b_bol", blob: blob });
    } catch (e) { console.warn("[traceability] annex B failed:", e && e.message); }
    /* Annex C — Receiving Sheet */
    try {
      const html = buildReceivingSheetHTML(shipment, lookups.suppliers, lang);
      const blob = await htmlToPdfBlob(html, { orientation: "portrait" });
      items.push({ name: "annex_c_receiving", blob: blob });
    } catch (e) { console.warn("[traceability] annex C failed:", e && e.message); }
    /* Annexes D + E — only if we have an invoice */
    if (invoice) {
      try {
        const ctx = { clients: lookups.clients, shipments: shipments, warehouses: lookups.warehouses, company: lookups.company };
        const htmlD = buildPackingListHTML(invoice, ctx, { brandKey: "gfi" });
        const blobD = await htmlToPdfBlob(htmlD, { orientation: "portrait" });
        items.push({ name: "annex_d_packing", blob: blobD });
      } catch (e) { console.warn("[traceability] annex D failed:", e && e.message); }
      try {
        const client = (lookups.clients || []).find(function (c) { return String(c.id) === String(invoice.clientId); });
        const htmlE = buildInvoicePrintHTML(invoice, client);
        const blobE = await htmlToPdfBlob(htmlE, { orientation: "portrait" });
        items.push({ name: "annex_e_invoice", blob: blobE });
      } catch (e) { console.warn("[traceability] annex E failed:", e && e.message); }
    }
    return mergePdfBlobs(items);
  }, [shipment, invoice, lookups, shipments, shipperPdfBlob, buildCover, lang]);

  const currentParams = useMemo(() => ({ shipmentId, invoiceId }), [shipmentId, invoiceId]);

  const handlePdf = async () => {
    if (busy || !shipment) return;
    setBusy(true);
    try {
      const blob = await buildCombinedPdf();
      const fn = safeFilename("Traceability_Lot_" + (shipment.lot || "X") + "_" + (audit && audit.name || "")) + ".pdf";
      lastBlobRef.current = blob; lastFilenameRef.current = fn;
      downloadBlob(blob, fn);
      onLogReport({
        type: "traceability",
        title: (lang === "es" ? "PDF combinado · Lote " : "Combined PDF · Lot ") + (shipment.lot || "—"),
        params: currentParams,
        blob: blob, filename: fn, mime: "application/pdf",
      });
    } catch (e) {
      console.error("[Traceability] pdf failed", e);
      window.alert((lang === "es" ? "Error: " : "Error: ") + (e.message || String(e)));
    } finally { setBusy(false); }
  };

  const [emailProfile, setEmailProfile] = useState({});
  useEffect(() => {
    let alive = true;
    loadFromSupabase("company_profile")
      .then(function (rows) {
        if (!alive) return;
        var p = (rows || []).find(function (r) { return String(r.id) === "gfi"; }) || (rows || [])[0] || {};
        setEmailProfile(p);
      })
      .catch(function () {});
    return function () { alive = false; };
  }, []);
  const profile = (emailProfile && Object.keys(emailProfile).length > 0) ? emailProfile : (lookups.company || {});
  const signatures = (profile && profile.emailSignatures) || [];
  const fromEmail  = (profile && (profile.outboundEmail || profile.email)) || "";
  /* 2026-09-30 — excludes Sweetleaf/automated accounts, same fix as
     OrganicAuditRegistry.js's own emailAccounts. */
  const emailAccounts = ((profile && profile.emailAccounts) || []).filter(function (a) { var n = (a.name || "").toLowerCase(); return n.indexOf("sweetleaf") < 0 && n.indexOf("automat") < 0; });

  const coverHtml = useMemo(() => buildCover(), [buildCover]);

  return (
    <DraggableModal title={t.title + " · " + (audit && audit.name || "")} onClose={onClose} zIndex={10003}>
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{t.selectShipment}</div>
            <SearchSelect options={shipmentOptions} value={shipmentId} onChange={setShipmentId} placeholder={t.selectShipmentPh} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{t.selectInvoice}</div>
            <SearchSelect options={invoiceOptions} value={invoiceId} onChange={setInvoiceId} placeholder={t.selectInvoicePh} disabled={!shipment} />
          </div>
        </div>

        {!shipment ? (
          <div style={{ padding: 30, textAlign: "center", color: "var(--color-text-muted)", fontStyle: "italic", border: "1px dashed var(--color-border)", borderRadius: 8 }}>{t.chooseFirst}</div>
        ) : (
          <>
            {!invoice && (
              <div style={{ padding: "8px 12px", borderRadius: 6, background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.30)", color: "#92400E", fontSize: 12, marginBottom: 12 }}>
                {t.noInvoice}
              </div>
            )}
            <div style={{ border: "1px solid var(--color-border)", borderRadius: 6, overflow: "hidden", background: "#fff", height: 380 }}>
              <iframe title="traceability-preview" srcDoc={coverHtml} style={{ width: "100%", height: "100%", border: 0 }} />
            </div>
          </>
        )}
      </div>
      <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{busy ? t.sending : ""}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={btnSecondary}>{t.close}</button>
          <button onClick={handlePreview} style={btnSecondary} disabled={!shipment}>{t.btnPreview}</button>
          <button onClick={() => setShowEmail(true)} style={btnSecondary} disabled={!shipment}>{t.btnEmail}</button>
          <button onClick={handlePdf} style={btnPrimary} disabled={busy || !shipment}>{t.btnPdf}</button>
        </div>
      </div>

      {showEmail && (
        <EmailDialog
          lang={lang}
          title={t.btnEmail}
          recipients={(audit && audit.auditorEmail) ? [{ name: audit.auditorName || "", email: audit.auditorEmail, checked: true }] : []}
          docOptions={[{ key: "report", label: t.title, checked: true }]}
          defaultSubject={t.emailSubject + " — " + (audit && audit.name || "")}
          defaultBody={lang === "es"
            ? "Adjunto la muestra de trazabilidad para el lote " + (shipment && shipment.lot || "—") + " (auditoría " + (audit && audit.name || "") + ")."
            : "Attached is the traceability sample for lot " + (shipment && shipment.lot || "—") + " (audit " + (audit && audit.name || "") + ")."}
          fromEmail={fromEmail}
          emailAccounts={emailAccounts}
          signatures={signatures}
          postProcessHtmlBody={(html, resolvedFrom) => appendDisclosureByAccount(html, resolvedFrom, profile, null)}
          buildAttachments={async () => {
            const blob = await buildCombinedPdf();
            const fn = safeFilename("Traceability_Lot_" + (shipment && shipment.lot || "X") + "_" + (audit && audit.name || "")) + ".pdf";
            lastBlobRef.current = blob; lastFilenameRef.current = fn;
            return [{ filename: fn, blob: blob, mimeType: "application/pdf" }];
          }}
          onSent={() => {
            const to = (audit && audit.auditorEmail) ? [audit.auditorEmail] : [];
            onLogReport({
              type: "traceability",
              title: (lang === "es" ? "Enviado por correo · Lote " : "Sent by email · Lot ") + (shipment && shipment.lot || "—"),
              recipients: to,
              params: currentParams,
              blob: lastBlobRef.current, filename: lastFilenameRef.current, mime: "application/pdf",
            });
            setShowEmail(false);
          }}
          onClose={() => setShowEmail(false)}
        />
      )}
    </DraggableModal>
  );
}
